import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import Tooltip from '../Tooltip'
import DownloadIcon from '../icons/DownloadIcon'
import CloseIcon from '../icons/CloseIcon'
import ChevronLeftIcon from '../icons/ChevronLeftIcon'

const VIEWER_Z = 1060
const OVERLAY_BG = 'rgba(15, 15, 15, 0.9)'

const OVERLAY_CLASS = 'tw:fixed tw:inset-0 tw:flex tw:flex-col tw:items-center tw:gap-8 '
  + 'tw:px-42 tw:py-10 tw:focus:outline-none'
const COUNTER_CLASS = 'tw:m-0! tw:text-sm tw:font-medium tw:text-white'
const FRAME_CLASS = 'tw:flex tw:flex-1 tw:min-h-0 tw:w-full tw:items-center tw:justify-center '
  + 'tw:bg-black tw:rounded-lg! tw:p-3'
const IMAGE_CLASS = 'tw:max-w-full tw:max-h-full tw:rounded-lg!'
const PANORAMA_CLASS = 'tw:w-full tw:h-full tw:rounded-lg!'

const ACTIONS_CLASS = 'tw:absolute tw:top-10 tw:right-10 tw:flex tw:items-center tw:gap-2'
const ACTION_CLASS = 'tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center tw:rounded! '
  + 'tw:bg-transparent tw:border-0 tw:p-0 tw:text-xl! tw:text-white! tw:cursor-pointer '
  + 'tw:hover:bg-viewer-hover! tw:active:bg-viewer-pressed!'
const DOWNLOAD_ICON_CLASS = 'tw:text-md'

const PREVIOUS_WRAP_CLASS = 'tw:absolute tw:left-24 tw:top-1/2 tw:-translate-y-1/2'
const NEXT_WRAP_CLASS = 'tw:absolute tw:right-24 tw:top-1/2 tw:-translate-y-1/2'
const ARROW_CLASS = 'tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center tw:rounded-full! '
  + 'tw:bg-white tw:border-0 tw:p-0 tw:text-2xl! tw:text-gray-900 tw:cursor-pointer '
  + 'tw:hover:bg-gray-100'
const NEXT_ICON_CLASS = 'tw:rotate-180'

const NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight']
// The overlay only holds the action links and the navigation buttons.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])'

const handledKey = (key, total) =>
  key === 'Escape' || key === 'Tab' || total > 1 && NAVIGATION_KEYS.includes(key)

const noop = () => null

// Keeps Tab inside the overlay, as Drawer does for its panel. The overlay itself holds the focus
// On opening, so a first Shift+Tab has to be caught too, otherwise it lands behind the overlay.
const trapTab = (event, overlay) => {
  const focusables = Array.from(overlay?.querySelectorAll(FOCUSABLE_SELECTOR) || [])
  if (focusables.length === 0)
    return
  const [first] = focusables
  const last = focusables[focusables.length - 1]
  if (event.shiftKey && (document.activeElement === first || document.activeElement === overlay)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

const counterLabel = (index, total) => `${index + 1}/${total} photo${total > 1 ? 's' : ''}`

const PhotoViewer = ({ photos, isOpen, startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex)
  // Url of a panorama the 360 viewer could not render, shown as a flat image instead.
  const [failedPanorama, setFailedPanorama] = useState(null)
  const overlayRef = useRef(null)
  const triggerRef = useRef(null)
  const panoramaRef = useRef(null)

  const total = photos.length
  const isVisible = isOpen && total > 0
  const goPrevious = useCallback(() => setIndex(current => (current - 1 + total) % total), [total])
  const goNext = useCallback(() => setIndex(current => (current + 1) % total), [total])

  // Syncing in an effect would paint one frame on the previous index and fetch the wrong photo.
  const [opening, setOpening] = useState({ isVisible, startIndex })
  if (opening.isVisible !== isVisible || opening.startIndex !== startIndex) {
    setOpening({ isVisible, startIndex })
    setIndex(startIndex)
  }

  useEffect(() => {
    if (!isVisible)
      return noop
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'
    return () => { root.style.overflow = previousOverflow }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible)
      return noop
    triggerRef.current = document.activeElement
    overlayRef.current?.focus({ preventScroll: true })
    return () => {
      const trigger = triggerRef.current
      if (trigger && typeof trigger.focus === 'function')
        trigger.focus({ preventScroll: true })
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible)
      return noop
    const handler = event => {
      if (!handledKey(event.key, total))
        return
      event.stopPropagation()
      if (event.key === 'Tab')
        trapTab(event, overlayRef.current)
      else if (event.key === 'Escape')
        onClose()
      else if (event.key === 'ArrowLeft')
        goPrevious()
      else
        goNext()
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [isVisible, total, onClose, goPrevious, goNext])

  const handleOverlayClick = useCallback(event => {
    if (event.target === event.currentTarget)
      onClose()
  }, [onClose])

  const photo = photos[index] || photos[0] || null
  const panorama = photo?.spherical && photo.url !== failedPanorama ? photo.url : null

  const setPanoramaNode = useCallback(node => {
    if (panoramaRef.current) {
      panoramaRef.current.destroy()
      panoramaRef.current = null
    }
    if (!node || !panorama)
      return
    // A viewer that is missing, throws, or fails to load falls back to the flat image.
    try {
      panoramaRef.current = new window.PhotoSphereViewer.Viewer({
        container: node, panorama, defaultZoomLvl: 0
      })
      panoramaRef.current.addEventListener('panorama-error', () => setFailedPanorama(panorama))
    } catch {
      setFailedPanorama(panorama)
    }
  }, [panorama])

  if (!isVisible)
    return null

  const panoramaView = <div ref={setPanoramaNode} className={PANORAMA_CLASS} />
  const flatView = <img alt={photo.alt || 'Photo'} className={IMAGE_CLASS} src={photo.url} />

  const content = (
    <div
      ref={overlayRef}
      aria-label="Photo en plein écran"
      aria-modal="true"
      className={OVERLAY_CLASS}
      role="dialog"
      style={{ backgroundColor: OVERLAY_BG, zIndex: VIEWER_Z }}
      tabIndex={-1}
      onClick={handleOverlayClick}
    >
      <div className={ACTIONS_CLASS}>
        <Tooltip content="Télécharger" placement="bottom">
          <a
            aria-label="Télécharger la photo"
            className={ACTION_CLASS}
            href={photo.downloadUrl || photo.url}
            download
          >
            <DownloadIcon className={DOWNLOAD_ICON_CLASS} />
          </a>
        </Tooltip>
        <Tooltip content="Fermer" placement="bottom">
          <button aria-label="Fermer" className={ACTION_CLASS} type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        </Tooltip>
      </div>
      {total > 1 ? (
        <Tooltip className={PREVIOUS_WRAP_CLASS} content="Précédent">
          <button aria-label="Photo précédente" className={ARROW_CLASS} type="button" onClick={goPrevious}>
            <ChevronLeftIcon />
          </button>
        </Tooltip>
      ) : null}
      {total > 1 ? (
        <Tooltip className={NEXT_WRAP_CLASS} content="Suivant">
          <button aria-label="Photo suivante" className={ARROW_CLASS} type="button" onClick={goNext}>
            <ChevronLeftIcon className={NEXT_ICON_CLASS} />
          </button>
        </Tooltip>
      ) : null}
      <p className={COUNTER_CLASS}>{counterLabel(index, total)}</p>
      <div className={FRAME_CLASS}>
        {panorama ? panoramaView : flatView}
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

PhotoViewer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  photos: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    downloadUrl: PropTypes.string,
    spherical: PropTypes.bool,
    url: PropTypes.string.isRequired
  })).isRequired,
  startIndex: PropTypes.number
}

PhotoViewer.defaultProps = {
  startIndex: 0
}

export default PhotoViewer
